import { useState } from 'react'
import { X, MessageSquare, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { trpc } from '~/utils/trpc'
import { useLocation } from '@tanstack/react-router'

const CATEGORIES = ['bug', 'feature', 'improvement', 'other'] as const
type Category = (typeof CATEGORIES)[number]

interface FeedbackModalProps {
  onClose: () => void
}

export function FeedbackModal({ onClose }: FeedbackModalProps) {
  const { t } = useTranslation()
  const location = useLocation()

  const [category, setCategory] = useState<Category | ''>('')
  const [message, setMessage] = useState('')

  const submitMutation = trpc.feedback.submit.useMutation({
    onSuccess: () => {
      toast.success(t('feedback.successTitle'), {
        description: t('feedback.successDescription'),
      })
      onClose()
    },
    onError: (err) => {
      toast.error(t('feedback.errorTitle'), {
        description: err.message || t('feedback.errorDescription'),
      })
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!category || !message.trim()) return

    submitMutation.mutate({
      category,
      message: message.trim(),
      pagePathname: location.pathname,
    })
  }

  const MESSAGE_MAX = 2000

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="feedback-title"
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="w-full max-w-md bg-[#0f0f15] border-2 border-[#00f0ff]/40 font-mono shadow-[8px_8px_0px_0px_rgba(0,240,255,0.15)]">
        {/* Window chrome */}
        <div className="flex items-center justify-between p-4 border-b border-[#1f1f25]">
          <div className="flex gap-2">
            <div className="w-3 h-3 rounded-full bg-[#00f0ff]" />
            <div className="w-3 h-3 rounded-full bg-[#a0a0b0]/30" />
            <div className="w-3 h-3 rounded-full bg-[#a0a0b0]/30" />
          </div>
          <span className="text-xs text-[#a0a0b0] uppercase tracking-wider">
            {t('feedback.windowTitle')}
          </span>
          <button
            onClick={onClose}
            className="text-[#a0a0b0] hover:text-white transition-colors"
            aria-label={t('common.close')}
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Header */}
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 border-2 border-[#00f0ff]/50 bg-[#00f0ff]/10">
              <MessageSquare size={18} className="text-[#00f0ff]" />
            </div>
            <div>
              <h2 id="feedback-title" className="text-white font-black text-base">
                {t('feedback.title')}
              </h2>
              <p className="text-[#a0a0b0] text-xs mt-0.5">
                {t('feedback.subtitle')}
              </p>
            </div>
          </div>

          {/* Category select */}
          <div className="space-y-1.5">
            <label
              htmlFor="feedback-category"
              className="block text-xs text-[#a0a0b0] uppercase tracking-wider"
            >
              {t('feedback.categoryLabel')} <span className="text-[#00f0ff]">*</span>
            </label>
            <select
              id="feedback-category"
              required
              value={category}
              onChange={(e) => setCategory(e.target.value as Category | '')}
              className="w-full bg-[#1a1a1f] border border-[#2f2f35] text-white text-sm px-3 py-2 focus:outline-none focus:border-[#00f0ff] focus:ring-2 focus:ring-[#00f0ff] transition-colors appearance-none cursor-pointer"
            >
              <option value="" disabled className="text-[#a0a0b0]">
                {t('feedback.categoryPlaceholder')}
              </option>
              {CATEGORIES.map((c) => (
                <option key={c} value={c} className="bg-[#1a1a1f]">
                  {t(`feedback.categories.${c}`)}
                </option>
              ))}
            </select>
          </div>

          {/* Message textarea */}
          <div className="space-y-1.5">
            <label
              htmlFor="feedback-message"
              className="block text-xs text-[#a0a0b0] uppercase tracking-wider"
            >
              {t('feedback.messageLabel')} <span className="text-[#00f0ff]">*</span>
            </label>
            <textarea
              id="feedback-message"
              required
              value={message}
              onChange={(e) => setMessage(e.target.value.slice(0, MESSAGE_MAX))}
              rows={4}
              placeholder={t('feedback.messagePlaceholder')}
              className="w-full bg-[#1a1a1f] border border-[#2f2f35] text-white text-sm px-3 py-2 focus:outline-none focus:border-[#00f0ff] focus:ring-2 focus:ring-[#00f0ff] transition-colors resize-none placeholder:text-[#a0a0b0]/50"
            />
            <p className="text-right text-[10px] text-[#a0a0b0]/60">
              {message.length}/{MESSAGE_MAX}
            </p>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={!category || !message.trim() || submitMutation.isPending}
            className="w-full py-2.5 text-sm font-bold uppercase tracking-wider border-2 border-[#00f0ff] text-[#00f0ff] hover:bg-[#00f0ff] hover:text-[#0f0f15] transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {submitMutation.isPending ? (
              <>
                <Loader2 size={14} className="animate-spin" />
                {t('feedback.sending')}
              </>
            ) : (
              t('feedback.submit')
            )}
          </button>
        </form>
      </div>
    </div>
  )
}
